/**
 * CustomerFormView Component
 * ממשק לקוח לצפייה וחתימה על טפסים
 * דף ציבורי ללא צורך באימות
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import SignaturePad from './SignaturePad';
import config from '../config/config';

const API_BASE = config.api.baseUrl;

const CustomerFormView = ({ token }) => {
  // Form state
  const [form, setForm] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // PDF state
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form input state
  const [fieldValues, setFieldValues] = useState({});
  const [signatureData, setSignatureData] = useState(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const signaturePadRef = useRef(null);

  // Load form data
  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/api/public/form/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('הטופס לא נמצא או שפג תוקפו');
          }
          throw new Error('שגיאה בטעינת הטופס');
        }

        const data = await response.json();
        setForm(data.form);
        setTemplate(data.template);
        setFieldValues(data.form.field_values || {});

        // Check if already signed
        if (data.form.status === 'signed') {
          setSubmitted(true);
        }

        // Load PDF
        setPdfUrl(`${API_BASE}/api/public/form/${token}/pdf`);

      } catch (err) {
        console.error('Error loading form:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadForm();
    }
  }, [token]);

  // Handle field value change
  const handleFieldChange = useCallback((fieldId, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  }, []);

  // Handle signature change
  const handleSignatureChange = useCallback((data) => {
    setSignatureData(data);
  }, []);

  // Validate form before submission
  const validateForm = useCallback(() => {
    if (!template) return { valid: false, errors: ['טעינת הטופס נכשלה'] };

    const errors = [];

    // Check required fields
    for (const field of template.fields) {
      if (field.required) {
        if (field.type === 'signature') {
          if (!signatureData) {
            errors.push('נדרשת חתימה');
          }
        } else if (!field.readonly) {
          const value = fieldValues[field.id];
          if (!value || (typeof value === 'string' && !value.trim())) {
            errors.push(`השדה "${field.label}" הוא שדה חובה`);
          }
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }, [template, fieldValues, signatureData]);

  // Submit form
  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      setSubmitError(validation.errors.join('\n'));
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const response = await fetch(`${API_BASE}/api/public/form/${token}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signature_data: signatureData,
          field_values: fieldValues
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'שגיאה בשליחת הטופס');
      }

      setSubmitted(true);

    } catch (err) {
      console.error('Error submitting form:', err);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Download signed PDF
  const handleDownload = async () => {
    try {
      window.open(`${API_BASE}/api/public/form/${token}/download`, '_blank');
    } catch (err) {
      console.error('Error downloading:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>טוען טופס...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2>שגיאה</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Form expired or invalid
  if (!form || !template) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>📄</div>
          <h2>הטופס לא זמין</h2>
          <p>הטופס שאתה מנסה לגשת אליו אינו קיים או שפג תוקפו.</p>
        </div>
      </div>
    );
  }

  // Success state (after signing)
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logo}>🔵 אפיקים מים</div>
          <h1 style={styles.title}>{template.name}</h1>
        </div>

        <div style={styles.successContainer}>
          <div style={styles.successIcon}>✅</div>
          <h2>הטופס נשלח בהצלחה!</h2>
          <p>תודה שחתמת על הטופס. עותק ישלח אליך במייל.</p>

          <button
            onClick={handleDownload}
            style={styles.downloadButton}
          >
            📥 הורד עותק חתום
          </button>
        </div>
      </div>
    );
  }

  // Find signature field
  const signatureField = template.fields.find(f => f.type === 'signature');

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logo}>🔵 אפיקים מים</div>
        <h1 style={styles.title}>{template.name}</h1>
        {template.description && (
          <p style={styles.description}>{template.description}</p>
        )}
      </div>

      {/* Form Content */}
      <div style={styles.content}>
        {/* PDF Preview */}
        <div style={styles.pdfContainer}>
          <div style={styles.pdfHeader}>
            <span>עמוד {currentPage} מתוך {totalPages}</span>
            <div style={styles.pdfNav}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                style={styles.navButton}
              >
                ◀
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                style={styles.navButton}
              >
                ▶
              </button>
            </div>
          </div>

          <div style={styles.pdfViewer}>
            {pdfUrl ? (
              <embed
                src={`${pdfUrl}#page=${currentPage}`}
                type="application/pdf"
                width="100%"
                height="600px"
                style={styles.pdfEmbed}
              />
            ) : (
              <div style={styles.noPdf}>
                <span>📄</span>
                <p>לא ניתן להציג את המסמך</p>
              </div>
            )}

            {/* Overlay editable fields */}
            {template.fields
              .filter(f => f.page === currentPage && !f.readonly && f.type !== 'signature')
              .map(field => (
                <div
                  key={field.id}
                  style={{
                    ...styles.fieldOverlay,
                    left: `${field.position.x}px`,
                    top: `${field.position.y}px`,
                    width: `${field.position.width}px`,
                    height: `${field.position.height}px`
                  }}
                >
                  {field.type === 'text' && (
                    <textarea
                      value={fieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      placeholder={field.label}
                      style={styles.fieldInput}
                    />
                  )}
                  {field.type === 'date' && (
                    <input
                      type="date"
                      value={fieldValues[field.id] || ''}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      style={styles.fieldInput}
                    />
                  )}
                  {field.type === 'checkbox' && (
                    <input
                      type="checkbox"
                      checked={fieldValues[field.id] || false}
                      onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                      style={styles.checkbox}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Editable Fields Section */}
        <div style={styles.fieldsSection}>
          <h3 style={styles.sectionTitle}>פרטים לעדכון</h3>

          {template.fields
            .filter(f => !f.readonly && f.type !== 'signature')
            .map(field => (
              <div key={field.id} style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>
                  {field.label}
                  {field.required && <span style={styles.required}>*</span>}
                </label>

                {field.type === 'text' && (
                  <textarea
                    value={fieldValues[field.id] || ''}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    placeholder={`הכנס ${field.label}`}
                    style={styles.textarea}
                    rows={3}
                  />
                )}

                {field.type === 'date' && (
                  <input
                    type="date"
                    value={fieldValues[field.id] || ''}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    style={styles.input}
                  />
                )}

                {field.type === 'checkbox' && (
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={fieldValues[field.id] || false}
                      onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                    />
                    <span>{field.label}</span>
                  </label>
                )}
              </div>
            ))}
        </div>

        {/* Signature Section */}
        {signatureField && (
          <div style={styles.signatureSection}>
            <SignaturePad
              width={400}
              height={150}
              label={signatureField.label || 'חתימה'}
              required={signatureField.required}
              onSignatureChange={handleSignatureChange}
              animate={signatureField.required && !signatureData}
            />
          </div>
        )}

        {/* Error message */}
        {submitError && (
          <div style={styles.errorMessage}>
            <span>⚠️</span>
            <span>{submitError}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={styles.actions}>
          <button
            onClick={() => window.open(pdfUrl, '_blank')}
            style={styles.secondaryButton}
          >
            📥 הורדת מסמך
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              ...styles.primaryButton,
              opacity: submitting ? 0.7 : 1
            }}
          >
            {submitting ? 'שולח...' : '✅ אישור ושליחה'}
          </button>
        </div>

        {/* Note */}
        <p style={styles.note}>
          * נא לחתום על המסמך לפני השליחה
        </p>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
    fontFamily: 'Salesforce Sans, Arial, sans-serif',
    direction: 'rtl',
    padding: '20px'
  },

  header: {
    textAlign: 'center',
    padding: '30px 20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '20px',
    maxWidth: '900px',
    margin: '0 auto 20px'
  },

  logo: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#0176d3',
    marginBottom: '10px'
  },

  title: {
    margin: '10px 0',
    fontSize: '28px',
    color: '#181818'
  },

  description: {
    color: '#706e6b',
    fontSize: '14px',
    margin: 0
  },

  content: {
    maxWidth: '900px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    padding: '30px'
  },

  pdfContainer: {
    marginBottom: '30px'
  },

  pdfHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #e5e5e5',
    marginBottom: '15px'
  },

  pdfNav: {
    display: 'flex',
    gap: '8px'
  },

  navButton: {
    background: '#f3f3f3',
    border: '1px solid #d8dde6',
    borderRadius: '4px',
    padding: '5px 12px',
    cursor: 'pointer',
    fontSize: '14px'
  },

  pdfViewer: {
    position: 'relative',
    border: '1px solid #d8dde6',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#f5f5f5'
  },

  pdfEmbed: {
    display: 'block',
    border: 'none'
  },

  noPdf: {
    height: '400px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#706e6b',
    fontSize: '48px'
  },

  fieldOverlay: {
    position: 'absolute',
    background: 'rgba(255, 255, 200, 0.8)',
    border: '2px solid #f39c12',
    borderRadius: '4px'
  },

  fieldInput: {
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'transparent',
    padding: '4px',
    fontSize: '12px',
    resize: 'none'
  },

  checkbox: {
    width: '20px',
    height: '20px',
    margin: 'auto',
    display: 'block'
  },

  fieldsSection: {
    borderTop: '1px solid #e5e5e5',
    paddingTop: '20px',
    marginBottom: '30px'
  },

  sectionTitle: {
    color: '#181818',
    fontSize: '18px',
    marginBottom: '15px'
  },

  fieldGroup: {
    marginBottom: '20px'
  },

  fieldLabel: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 500,
    color: '#181818'
  },

  required: {
    color: '#c23934',
    marginRight: '4px'
  },

  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d8dde6',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box'
  },

  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d8dde6',
    borderRadius: '4px',
    fontSize: '14px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },

  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },

  signatureSection: {
    borderTop: '1px solid #e5e5e5',
    paddingTop: '20px',
    marginBottom: '30px'
  },

  errorMessage: {
    background: '#fef3f2',
    border: '1px solid #c23934',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#c23934',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    whiteSpace: 'pre-line'
  },

  actions: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'center',
    marginTop: '30px'
  },

  primaryButton: {
    background: 'linear-gradient(135deg, #0176d3 0%, #0b5cab 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 40px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'transform 0.2s, box-shadow 0.2s'
  },

  secondaryButton: {
    background: 'white',
    color: '#0176d3',
    border: '2px solid #0176d3',
    borderRadius: '8px',
    padding: '12px 30px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  note: {
    textAlign: 'center',
    color: '#706e6b',
    fontSize: '12px',
    marginTop: '15px'
  },

  // Loading state
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    color: '#706e6b'
  },

  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e5e5',
    borderTop: '4px solid #0176d3',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '15px'
  },

  // Error state
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80vh',
    textAlign: 'center'
  },

  errorIcon: {
    fontSize: '64px',
    marginBottom: '20px'
  },

  // Success state
  successContainer: {
    maxWidth: '500px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    padding: '40px',
    textAlign: 'center'
  },

  successIcon: {
    fontSize: '64px',
    marginBottom: '20px'
  },

  downloadButton: {
    background: 'linear-gradient(135deg, #2e844a 0%, #1b5e2c 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 30px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '20px'
  }
};

export default CustomerFormView;
